// Teable Onboarding Helper
// Utility function to trigger Teable onboarding after organization creation

type OnboardingStatus = {
  tenantId: string;
  spaceId: string | null;
  bases: Record<string, unknown>;
  isOnboarded: boolean;
};

/**
 * Check onboarding status for a tenant
 */
const checkOnboardingStatus = async (tenantId: string): Promise<OnboardingStatus | null> => {
  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const { supabase } = await import('../utils/supabaseClient');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      return null;
    }

    const response = await fetch(`${apiUrl}/api/onboarding/status/${tenantId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Error checking onboarding status:', error);
    return null;
  }
};

/**
 * Wait for onboarding to complete by polling the status endpoint
 * @param tenantId - The tenant ID to check
 * @param onProgress - Optional callback for progress updates
 * @param maxWaitTime - Maximum time to wait in milliseconds (default: 2 minutes)
 * @param pollInterval - Interval between polls in milliseconds (default: 2 seconds)
 */
export const waitForOnboardingCompletion = async (
  tenantId: string,
  onProgress?: (status: OnboardingStatus) => void,
  maxWaitTime: number = 120000, // 2 minutes
  pollInterval: number = 2000 // 2 seconds
): Promise<OnboardingStatus | null> => {
  const startTime = Date.now();

  return new Promise((resolve) => {
    const poll = async () => {
      const elapsed = Date.now() - startTime;
      
      if (elapsed > maxWaitTime) {
        console.warn('⚠️  Onboarding status check timed out');
        resolve(null);
        return;
      }

      const status = await checkOnboardingStatus(tenantId);
      
      if (status) {
        if (onProgress) {
          onProgress(status);
        }

        if (status.isOnboarded) {
          console.log('✅ Onboarding completed:', {
            spaceId: status.spaceId,
            basesCount: status.bases ? Object.keys(status.bases).length : 0,
          });
          resolve(status);
          return;
        }
      }

      // Continue polling
      setTimeout(poll, pollInterval);
    };

    poll();
  });
};

/**
 * Trigger Teable onboarding for a newly created organization
 * This is called automatically after organization creation
 * @param waitForCompletion - If true, waits for onboarding to complete before resolving
 */
export const triggerTeableOnboarding = async (
  tenantId: string,
  tenantName: string,
  waitForCompletion: boolean = false
): Promise<OnboardingStatus | null> => {
  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const { supabase } = await import('../utils/supabaseClient');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      console.warn('⚠️  No auth token available for Teable onboarding');
      return null;
    }

    console.log(`🚀 Triggering Teable onboarding for tenant: ${tenantId} (${tenantName})`);

    const response = await fetch(`${apiUrl}/api/onboarding`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        tenantId,
        tenantName,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Teable onboarding failed:', response.status, errorText);
      return null;
    }

    const result = await response.json();
    if (result.success) {
      console.log('✅ Teable onboarding initiated:', {
        spaceId: result.spaceId,
        basesCount: result.bases ? Object.keys(result.bases).length : 0,
      });

      // If waitForCompletion is true, poll until onboarding is complete
      if (waitForCompletion) {
        return await waitForOnboardingCompletion(tenantId);
      }

      return {
        tenantId,
        spaceId: result.spaceId || null,
        bases: result.bases || {},
        isOnboarded: !!result.spaceId,
      };
    } else {
      console.warn('⚠️  Teable onboarding completed with errors:', result.error);
      return null;
    }
  } catch (error) {
    console.error('❌ Error triggering Teable onboarding:', error);
    return null;
  }
};

